import React from 'react';

/**
 * CandidateReadinessCard - compact summary of the candidate's environment
 * readiness, populated by the `readiness_update` WS event from the desktop agent.
 *
 * Expected `readiness` shape (Phase 1):
 *   {
 *     micOk:       boolean,
 *     cameraOk:    boolean,
 *     networkMbps: number,   // download
 *     pingMs:      number,
 *     lockdownOk:  boolean,  // window-affinity / sandbox checks
 *     warnings:    string[], // non-blocking issues
 *     reportedAt:  ISO string
 *   }
 *
 * The component degrades gracefully when fields are missing.
 */
export default function CandidateReadinessCard({ readiness }) {
  if (!readiness || Object.keys(readiness).length === 0) {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-[11px] text-slate-500">
        Awaiting candidate readiness check…
      </div>
    );
  }

  const items = [
    { key: 'micOk',      label: 'Microphone' },
    { key: 'cameraOk',   label: 'Camera' },
    { key: 'lockdownOk', label: 'Secure mode' },
  ];

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
          Candidate Readiness
        </p>
        {readiness.reportedAt && (
          <span className="text-[10px] text-slate-500">
            {new Date(readiness.reportedAt).toLocaleTimeString()}
          </span>
        )}
      </div>

      <ul className="space-y-1">
        {items.map(({ key, label }) => (
          <ReadinessRow key={key} label={label} ok={readiness[key]} />
        ))}
      </ul>

      {(readiness.networkMbps != null || readiness.pingMs != null) && (
        <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-800 text-[10px]">
          {readiness.networkMbps != null && (
            <Metric label="Down" value={`${Number(readiness.networkMbps).toFixed(1)} Mbps`} />
          )}
          {readiness.pingMs != null && (
            <Metric label="Ping" value={`${Math.round(readiness.pingMs)} ms`} />
          )}
        </div>
      )}

      {readiness.warnings?.length > 0 && (
        <ul className="text-[10px] text-amber-300 space-y-0.5 pt-1 border-t border-slate-800">
          {readiness.warnings.map((w, i) => <li key={i}>⚠ {w}</li>)}
        </ul>
      )}
    </div>
  );
}

function ReadinessRow({ label, ok }) {
  const dot = ok === true  ? 'bg-emerald-500'
            : ok === false ? 'bg-red-500'
            :                'bg-slate-600';
  return (
    <li className="flex items-center justify-between text-[11px] text-slate-300">
      <span className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${dot}`} />
        {label}
      </span>
      <span className="text-slate-500">
        {ok === true ? 'OK' : ok === false ? 'Issue' : 'Unknown'}
      </span>
    </li>
  );
}

function Metric({ label, value }) {
  return (
    <div className="text-slate-400">
      <span className="text-slate-500">{label}: </span>
      <span className="text-slate-200 font-mono">{value}</span>
    </div>
  );
}
